/**
 * Предфильтр модерируемого чата ИИ-матча: то, что сервер отсекает сам, ДО
 * очереди модератора (src/utils/chat/chatModerationQueue.js).
 *
 * Сюда вынесено то, что надежно ловится без понимания текста и что нельзя
 * пропускать даже на проверку: модератор — LLM-агент, он читает недоверенный
 * текст, и чем меньше опасного до него доходит, тем лучше.
 *  - Пустое, слишком длинное, со служебными символами (управляющие, смена
 *    направления письма — ими подменяют видимый текст; теги и цепочки
 *    селекторов вариантов — ими прячут от людей текст, который LLM
 *    прочитает) — INVALID.
 *  - Похожее на ключ, токен или секрет (в том числе любой из переданных
 *    сервером секретов — служебные токены ИИ и модератора) — PREFILTER_SECRET:
 *    агент не должен суметь выболтать токен в чат даже по ошибке.
 *  - Ссылки — PREFILTER_LINK: в чат промо-матча ссылки не нужны, а через них
 *    идут спам и фишинг.
 *
 * Ссылки ищутся без ложных срабатываний на игровые записи: координаты
 * «1,-3», числа «3.5», «6-го», сокращения «т.е.» и «и т.д.» — ссылкой не
 * считаются. Голый домен без пути ловится только по списку ходовых зон
 * (LINK_TLDS): иначе «ok.then» без пробела выглядел бы ссылкой. Домен с путем
 * («что-то.xyz/abc») — ссылка при любой зоне.
 *
 * Модуль чистый, общий для сервера и клиента (клиент может заранее
 * подсказать игроку, что сообщение не уйдет).
 */

import {CHAT_REJECTION_REASONS, MODERATED_CHAT_MAX_LENGTH} from '../../configs/matchChat';

// Переданные секреты короче этого не проверяются: короткая строка совпала бы
// с обычными словами, а настоящие токены всегда длиннее
export const CHAT_PREFILTER_MIN_SECRET_LENGTH = 8;
// Сплошная строка из символов base64/hex не короче этого — похожа на токен
export const CHAT_PREFILTER_TOKEN_MIN_LENGTH = 32;

// Невидимые символы — все, что Unicode велит не отображать (Default_Ignorable_Code_Point):
// мягкий перенос, нулевой ширины, селекторы вариантов, пустые буквы хангыля,
// теги и т. п. Для поиска ссылок и секретов их выкидываем — иначе «t.me» с
// любым из них внутри прошел бы мимо. Перечислять вручную нельзя: пропущенный
// селектор варианта (U+FE0F) уже пропускал «t.me/...». В самом сообщении они
// остаются (соединитель нулевой ширины, например, склеивает составные эмодзи).
// Символы — escape-последовательностями, а не как есть: невидимые и
// разворачивающие текст символы в исходнике не видны при чтении и ревью
const INVISIBLE_CHARS_RE = /\p{Default_Ignorable_Code_Point}/gu;
// Точки, которыми подменяют обычную в адресах (NFKC сводит не все)
const LOOKALIKE_DOTS_RE = /[\u{2024}\u{3002}\u{FF0E}\u{FF61}]/gu;
// Смена направления письма: текст на экране читается не так, как у
// модератора, — такое сообщение отклоняется целиком
const BIDI_CONTROL_RE = /[\u{202A}-\u{202E}\u{2066}-\u{2069}]/u;
// Скрытый текст для LLM: теги U+E0000–U+E007F повторяют ASCII и читаются
// моделью, но не видны людям, — так в «безобидное» сообщение прячут
// инструкцию модератору или ИИ-игроку. Туда же дополнительные селекторы
// вариантов U+E0100–U+E01EF и два селектора подряд (байты, спрятанные за
// эмодзи): в живом тексте селектор один и стоит после видимого символа.
// Цена — флаги Англии, Шотландии и Уэльса (они собраны из тегов) не проходят
const HIDDEN_PAYLOAD_RE = /[\u{E0000}-\u{E0FFF}]|[\u{FE00}-\u{FE0F}]{2}/u;

// Зоны, голый домен в которых без пути считается ссылкой. Без английских
// слов-зон вроде so/to/us/in/game: «well.so» без пробела — опечатка, а не
// адрес. И без gg/me/ly: «спасибо за игру.gg» в игровом чате пишут часто, а
// discord.gg, t.me и bit.ly без пути никуда не ведут — с путем их ловит
// правило «домен с путем»
export const CHAT_PREFILTER_LINK_TLDS = [
  'com', 'net', 'org', 'ru', 'su', 'ua', 'by', 'kz', 'uz', 'kg', 'md', 'ge', 'az',
  'io', 'co', 'tv', 'cc', 'xyz', 'info', 'biz', 'pro', 'app',
  'dev', 'site', 'online', 'store', 'shop', 'link', 'space', 'tech', 'website',
  'page', 'cloud', 'ai', 'eu', 'uk', 'de', 'fr', 'pl', 'cn', 'jp', 'ws', 'tk',
  'ml', 'ga', 'cf', 'gq', 'sh', 'gd', 'bz', 'icu', 'vip', 'bet', 'cash', 'media',
  'news', 'blog', 'email', 'network', 'zone', 'bio', 'club',
];
// Кириллические зоны — только целиком из списка: «ок.давай» не адрес
export const CHAT_PREFILTER_CYRILLIC_TLDS = ['рф', 'рус', 'москва', 'онлайн', 'сайт', 'укр', 'бел'];

// Метка домена — латиница, цифры, дефис и кириллица (кириллические домены
// тоже бывают: «сайт.рф»)
const DOMAIN_LABEL = '[a-z0-9а-яё-]+';
const DOMAIN_LABELS = `(?:${DOMAIN_LABEL}\\.)+`;

const LINK_PATTERNS = [
  // Любая схема: http://, https://, ftp://, tg:// ...
  /[a-z][a-z0-9+.-]*:\/\//,
  // www. — уже адрес, даже без зоны
  /(?:^|[^a-z0-9])www\d{0,3}\./,
  // IPv4: четыре числа через точку (три — это еще «версия 1.2.3»)
  /(?:^|[^0-9.])\d{1,3}(?:\.\d{1,3}){3}(?![0-9])/,
  // Домен с путем — при любой латинской зоне: t.me/..., discord.gg/...
  new RegExp(`${DOMAIN_LABELS}[a-z]{2,24}/`),
  // Голый домен ходовой латинской зоны. Справа — не латиница и не цифра:
  // «example.comпривет» тоже ссылка
  new RegExp(`${DOMAIN_LABELS}(?:${CHAT_PREFILTER_LINK_TLDS.join('|')})(?![a-z0-9])`),
  // Голый домен кириллической зоны
  new RegExp(`${DOMAIN_LABELS}(?:${CHAT_PREFILTER_CYRILLIC_TLDS.join('|')})(?![a-z0-9а-яё])`),
];

// Ключи известных форматов — ловятся и короче CHAT_PREFILTER_TOKEN_MIN_LENGTH
const SECRET_PATTERNS = [
  // Ключи в стиле sk-...: OpenAI, Anthropic (sk-ant-...) и подобные
  /(?:^|[^A-Za-z0-9])sk-[A-Za-z0-9_-]{16,}/,
  // Stripe: sk_live_..., rk_test_...
  /(?:^|[^A-Za-z0-9])[sr]k_(?:live|test)_[A-Za-z0-9]{10,}/,
  // GitHub
  /gh[pousr]_[A-Za-z0-9]{20,}/,
  /github_pat_[A-Za-z0-9_]{20,}/,
  // Slack
  /xox[abposr]-[A-Za-z0-9-]{10,}/,
  // AWS: идентификатор ключа доступа
  /(?:AKIA|ASIA)[0-9A-Z]{16}/,
  // Google API
  /AIza[0-9A-Za-z_-]{30,}/,
  // JWT: заголовок и тело в base64url, заголовок всегда начинается с eyJ
  /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/,
  // Приватный ключ PEM
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
];

// Сплошные строки из символов base64/base64url/hex
const TOKEN_RUN_RE = new RegExp(`[A-Za-z0-9+/=_-]{${CHAT_PREFILTER_TOKEN_MIN_LENGTH},}`, 'g');

/**
 * Текст сообщения в том виде, в каком его проверяют и доставляют: без
 * пробелов по краям, перевод строки — только \n. Одиночный \r для части
 * читателей тоже перевод строки (Markdown отчета и PR, модель), а построчная
 * обработка его не видит: «ок\r## …» в хронике матча выскочил бы из цитаты
 * чата отдельным заголовком. Не строка — пустая строка. В отличие от
 * normalizeChatMessageText (chatMessage.js), длинный текст не обрезается:
 * в модерируемом чате он отклоняется целиком — иначе модератор одобрил бы
 * не то, что игрок написал.
 */
export const normalizeModeratedChatText = (text) => (
  typeof text === 'string' ? text.replace(/\r\n?/g, '\n').trim() : ''
);

// Управляющие символы, кроме табуляции и переводов строки: C0, DEL и C1
// (U+0080–U+009F, среди них NEL — перевод строки для части программ)
const checkIfHasControlChars = (text) => {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if ((code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) || (code >= 0x7f && code <= 0x9f)) {
      return true;
    }
  }
  return false;
};

const stripInvisibleChars = (text) => text.replace(INVISIBLE_CHARS_RE, '');

// Текст для поиска ссылок: без невидимых символов, полноширинные и прочие
// «похожие» буквы и точки сведены к обычным, нижний регистр
const toLinkSearchText = (visibleText) => visibleText
  .normalize('NFKC')
  .replace(LOOKALIKE_DOTS_RE, '.')
  .toLowerCase();

/**
 * Похожа ли сплошная строка на токен, а не на слово: в ней есть и цифра, и
 * латинская буква. Длинный смех «хахаха»/«hahaha» и длинные числа так не
 * отсекаются, а случайный ключ из 32+ символов почти наверняка содержит и то
 * и другое.
 */
const checkIfTokenLikeRun = (run) => /[0-9]/.test(run) && /[A-Za-z]/.test(run);

const checkIfHasTokenLikeString = (text) => {
  if (SECRET_PATTERNS.some((pattern) => pattern.test(text))) {
    return true;
  }
  const runs = text.match(TOKEN_RUN_RE) || [];
  return runs.some(checkIfTokenLikeRun);
};

const checkIfHasProvidedSecret = (texts, secrets) => (Array.isArray(secrets) ? secrets : [])
  .filter((secret) => typeof secret === 'string' && secret.length >= CHAT_PREFILTER_MIN_SECRET_LENGTH)
  .some((secret) => texts.some((text) => text.includes(secret)));

/**
 * Проверка сообщения до очереди модератора.
 *
 * Порядок причин: сначала INVALID (такое не принимается вообще), затем
 * секрет, затем ссылка — если в сообщении и то и другое, важнее причина
 * «секрет»: по ней сервер видит утечку в логах.
 *
 * @param {string} text - текст сообщения как его прислал клиент
 * @param {Object} [options]
 * @param {string[]} [options.secrets] - строки, которых не должно быть в
 *   чате (служебные токены); регистр важен, короче 8 символов — игнорируются
 * @returns {{ok: true} | {ok: false, reason: string}}
 */
export const prefilterChatMessage = (text, {secrets = []} = {}) => {
  if (typeof text !== 'string') {
    return {ok: false, reason: CHAT_REJECTION_REASONS.INVALID};
  }
  const trimmed = normalizeModeratedChatText(text);
  const visible = stripInvisibleChars(trimmed);
  if (visible.trim().length === 0 || trimmed.length > MODERATED_CHAT_MAX_LENGTH) {
    return {ok: false, reason: CHAT_REJECTION_REASONS.INVALID};
  }
  if (checkIfHasControlChars(trimmed) || BIDI_CONTROL_RE.test(trimmed) || HIDDEN_PAYLOAD_RE.test(trimmed)) {
    return {ok: false, reason: CHAT_REJECTION_REASONS.INVALID};
  }

  if (checkIfHasProvidedSecret([trimmed, visible], secrets)
    || checkIfHasTokenLikeString(visible.normalize('NFKC'))) {
    return {ok: false, reason: CHAT_REJECTION_REASONS.PREFILTER_SECRET};
  }

  const linkSearchText = toLinkSearchText(visible);
  if (LINK_PATTERNS.some((pattern) => pattern.test(linkSearchText))) {
    return {ok: false, reason: CHAT_REJECTION_REASONS.PREFILTER_LINK};
  }

  return {ok: true};
};
